import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { toast } from "sonner";

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
  defaultRole?: "buyer" | "seller";
}

export function WaitlistModal({ open, onClose, defaultRole = "buyer" }: WaitlistModalProps) {
  const [role, setRole] = useState<"buyer" | "seller">(defaultRole);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setRole(defaultRole);
  }, [defaultRole, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Você está na lista. Em breve entraremos em contato.");
      setSubmitting(false);
      onClose();
    }, 800);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-scale-in"
      style={{ background: "color-mix(in oklab, oklch(0.1 0.02 275) 70%, transparent)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="glass-strong relative w-full max-w-lg rounded-3xl p-8 shadow-deep"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full bg-surface-2/60 p-2 text-muted-foreground transition-colors hover:bg-surface-3 hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-6">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary-glow">
            <Sparkles className="h-3 w-3" />
            Acesso Antecipado
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight">Em Breve</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Estamos aceitando usuários iniciais para a nossa lista de espera.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome completo" name="name" placeholder="Maria Silva" required />
            <Field label="E-mail" name="email" type="email" placeholder="voce@email.com" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone" name="phone" placeholder="+55 11 ..." required />
            <Field label="Redes sociais" name="social" placeholder="@perfil (opcional)" />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sou
            </label>
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-surface-1/60 p-1">
              {(["buyer", "seller"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${
                    role === r
                      ? "bg-gradient-primary text-primary-foreground shadow-elegant"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {r === "buyer" ? "Comprador" : "Vendedor"}
                </button>
              ))}
            </div>
          </div>

          {role === "seller" && (
            <div className="animate-fade-in-up">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Redes sociais
              </label>
              <input
                type="text"
                name="seller_social"
                placeholder="@seu_perfil, link do Instagram, TikTok..."
                required
                className="w-full rounded-xl bg-primary/10 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              O que você gostaria de ver na plataforma?
            </label>
            <textarea
              name="feedback"
              rows={3}
              placeholder="Conte o que te empolga..."
              className="w-full resize-none rounded-xl bg-surface-1/70 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="relative w-full overflow-hidden rounded-full bg-gradient-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-elegant transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
          >
            {submitting ? "Entrando..." : "Entrar na Lista"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  placeholder,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl bg-surface-1/70 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
    </div>
  );
}
